import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { UserPlus, RefreshCw, ToggleLeft, ToggleRight, Key } from 'lucide-react';
import { Switch } from '../ui/switch';

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [generatedOTP, setGeneratedOTP] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const response = await api.getStaff();
      setStaff(response.staff);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      await api.createStaff(formData);
      toast.success('Staff created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', email: '', phone: '', password: '' });
      loadStaff();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleEnabled = async (staffId, currentStatus) => {
    try {
      await api.updateStaff({ staff_id: staffId, enabled: !currentStatus });
      toast.success(`Staff ${!currentStatus ? 'enabled' : 'disabled'}`);
      loadStaff();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      await api.updateStaff({ staff_id: selectedStaff.id, password: resetPassword });
      toast.success('Password reset successfully');
      setShowResetDialog(false);
      setResetPassword('');
      setResetConfirmPassword('');
      setSelectedStaff(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleGenerateOTP = async (member) => {
    try {
      const response = await api.generateStaffOTP(member.id);
      setGeneratedOTP(response.otp);
      setSelectedStaff(member);
      setShowOTPDialog(true);
      toast.success('OTP generated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to generate OTP');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" data-testid="staff-management-title">Staff Management</h2>
          <p className="text-slate-600">Create and manage staff accounts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-staff-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Create Staff
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Staff Members</CardTitle>
          <CardDescription>Manage staff accounts and permissions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading staff...</div>
          ) : staff.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No staff members yet</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="staff-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.map((member) => (
                    <TableRow key={member.id} data-testid={`staff-row-${member.id}`}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.phone}</TableCell>
                      <TableCell>
                        <Badge variant={member.enabled ? 'default' : 'secondary'} data-testid={`staff-status-${member.id}`}>
                          {member.enabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateOTP(member)}
                            data-testid={`generate-otp-${member.id}`}
                          >
                            <Key className="w-4 h-4 mr-2" /> Get OTP
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleToggleEnabled(member.id, member.enabled)}
                            data-testid={`toggle-staff-${member.id}`}
                          >
                            {member.enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                            {member.enabled ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedStaff(member);
                              setShowResetDialog(true);
                            }}
                            data-testid={`reset-password-${member.id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" /> Reset Password
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Staff Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-staff-dialog">
          <DialogHeader>
            <DialogTitle>Create Staff Account</DialogTitle>
            <DialogDescription>Add a new staff member to the system</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateStaff} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                data-testid="staff-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="staff-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone (with country code)</Label>
              <Input
                id="phone"
                placeholder="+919876543210"
                data-testid="staff-phone-input"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="staff-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-create-staff">
                Create Staff
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Reset password for {selectedStaff?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                data-testid="confirm-password-input"
                value={resetConfirmPassword}
                onChange={(e) => setResetConfirmPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowResetDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-reset-password">
                Reset Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* OTP Dialog */}
      <Dialog open={showOTPDialog} onOpenChange={setShowOTPDialog}>
        <DialogContent data-testid="otp-dialog">
          <DialogHeader>
            <DialogTitle>Staff Login OTP</DialogTitle>
            <DialogDescription>
              Share this OTP with {selectedStaff?.name} for login
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-6 bg-blue-50 rounded-lg text-center">
              <p className="text-sm text-slate-600 mb-2">One-Time Password</p>
              <p className="text-4xl font-bold text-blue-600 tracking-widest" data-testid="generated-otp">
                {generatedOTP}
              </p>
            </div>
            <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700">Instructions:</p>
              <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                <li>Share this OTP with the staff member</li>
                <li>Staff logs in with email and password</li>
                <li>Staff enters this OTP when prompted</li>
                <li>OTP is valid for 10 minutes</li>
              </ol>
            </div>
            <Button onClick={() => setShowOTPDialog(false)} className="w-full" data-testid="close-otp-dialog">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
